function cloneValue(value) {
    return JSON.parse(JSON.stringify(value));
}

const CLIPBOARD_STORAGE_KEY = 'laoreng-editor-field-clipboard';
const ALLOWED_FIELD_TYPES = new Set([
    'text',
    'number',
    'dropdown',
    'checkbox',
    'date',
    'time',
    'signature',
    'image',
    'rectangle',
]);
const CLIPBOARD_FIELD_KEYS = [
    'id',
    'type',
    'key',
    'label',
    'pageIndex',
    'xNorm',
    'yNorm',
    'wNorm',
    'hNorm',
    'required',
    'placeholder',
    'subtext',
    'fontSize',
    'fontSizeMode',
    'wrapMode',
    'rangeMode',
    'align',
    'direction',
    'autoFill',
    'isMultiline',
    'isReadOnly',
    'staticValue',
    'suggestedValues',
    'assetDataUrl',
    'optionsJson',
    'numericConfigJson',
    'styleJson',
    'sectionId',
    'orderIndex',
    'sequenceNumber',
    'skipSequence',
    'tableRowGroupId',
    'tableRowIndex',
    'tableRowBaseId',
];
const PRESET_KEYLESS_TYPES = new Set(['image', 'rectangle', 'sectionBreak']);

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function sortFieldsForClipboard(fields = []) {
    return fields
        .map((field) => cloneValue(field))
        .sort((a, b) => {
            const pageDiff = (Number(a.pageIndex) || 0) - (Number(b.pageIndex) || 0);
            if (pageDiff !== 0) return pageDiff;
            const yDiff = (Number(a.yNorm) || 0) - (Number(b.yNorm) || 0);
            if (Math.abs(yDiff) > 0.0005) return yDiff;
            const xDiff = (Number(a.xNorm) || 0) - (Number(b.xNorm) || 0);
            if (Math.abs(xDiff) > 0.0005) return xDiff;
            const orderDiff = (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0);
            if (orderDiff !== 0) return orderDiff;
            return (Number(a.sequenceNumber) || 0) - (Number(b.sequenceNumber) || 0);
        });
}

function sanitizeClipboardField(field) {
    if (!field || typeof field !== 'object' || Array.isArray(field)) {
        return null;
    }
    const rawField = cloneValue(field);
    if (!ALLOWED_FIELD_TYPES.has(rawField.type)) {
        return null;
    }
    const sanitized = { type: rawField.type };
    CLIPBOARD_FIELD_KEYS.forEach((key) => {
        if (key === 'type') return;
        if (Object.prototype.hasOwnProperty.call(rawField, key)) {
            sanitized[key] = cloneValue(rawField[key]);
        }
    });
    return sanitized;
}

function normalizeFieldKeyBase(value) {
    const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^0-9a-z\u0590-\u05ff_-]+/gi, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '');
    return normalized || '';
}

function buildUniquePresetFieldKey(field, usedKeys, createId) {
    if (!field || PRESET_KEYLESS_TYPES.has(field.type)) {
        return field?.key || '';
    }
    const fallbackId = String(field.id || createId()).replace(/[^0-9a-z]/gi, '').toLowerCase();
    const shortId = fallbackId.slice(0, 8) || String(createId()).replace(/[^0-9a-z]/gi, '').toLowerCase().slice(0, 8);
    const baseKey =
        normalizeFieldKeyBase(field.key) ||
        normalizeFieldKeyBase(field.label) ||
        normalizeFieldKeyBase(field.type) ||
        'field';
    let candidate = `${baseKey}_${shortId}`;
    let counter = 2;
    while (usedKeys.has(candidate)) {
        candidate = `${baseKey}_${shortId}_${counter}`;
        counter += 1;
    }
    usedKeys.add(candidate);
    return candidate;
}

function buildSelectionClipboardPayload({ fields = [], templateId = null }) {
    return {
        type: 'laoreng-editor-fields',
        templateId: templateId || null,
        fields: sortFieldsForClipboard(fields),
    };
}

function serializeSelectionClipboardPayload(payload) {
    return JSON.stringify(payload, null, 2);
}

function parseSelectionClipboardPayload(rawValue) {
    if (!rawValue) return null;
    if (typeof rawValue === 'object') {
        if (Array.isArray(rawValue)) {
            const fields = rawValue.map((field) => sanitizeClipboardField(field)).filter(Boolean);
            return fields.length > 0
                ? { type: 'legacy-fields', templateId: null, fields }
                : null;
        }
        if (Array.isArray(rawValue.fields)) {
            const fields = rawValue.fields.map((field) => sanitizeClipboardField(field)).filter(Boolean);
            if (fields.length === 0) {
                return null;
            }
            return {
                type: rawValue.type || 'laoreng-editor-fields',
                templateId: rawValue.templateId || null,
                fields,
            };
        }
        return null;
    }
    if (typeof rawValue !== 'string') return null;
    try {
        const parsed = JSON.parse(rawValue);
        return parseSelectionClipboardPayload(parsed);
    } catch {
        return null;
    }
}

function writeSelectionClipboardPayload(payload) {
    const parsed = parseSelectionClipboardPayload(payload);
    if (!parsed?.fields?.length) {
        return null;
    }
    if (typeof window !== 'undefined') {
        window.__pdfFieldClipboard = parsed;
        try {
            window.localStorage.setItem(CLIPBOARD_STORAGE_KEY, serializeSelectionClipboardPayload(parsed));
        } catch {
            // ignore storage failures
        }
    }
    return parsed;
}

function readSelectionClipboardPayload() {
    if (typeof window === 'undefined') {
        return null;
    }
    const memoryPayload = parseSelectionClipboardPayload(window.__pdfFieldClipboard);
    if (memoryPayload?.fields?.length) {
        return memoryPayload;
    }
    try {
        const storedPayload = parseSelectionClipboardPayload(window.localStorage.getItem(CLIPBOARD_STORAGE_KEY));
        if (storedPayload?.fields?.length) {
            window.__pdfFieldClipboard = storedPayload;
            return storedPayload;
        }
    } catch {
        // ignore storage failures
    }
    return null;
}

function remapTableRowKeys(fields, createId) {
    const fieldIdMap = new Map();
    const groupIdMap = new Map();
    fields.forEach((field) => {
        fieldIdMap.set(String(field.id), createId());
        if (field.tableRowGroupId && !groupIdMap.has(String(field.tableRowGroupId))) {
            groupIdMap.set(String(field.tableRowGroupId), createId());
        }
    });
    return fields.map((field) => {
        const nextField = cloneValue(field);
        const nextId = fieldIdMap.get(String(field.id)) || createId();
        nextField.id = nextId;
        if (nextField.tableRowGroupId) {
            nextField.tableRowGroupId = groupIdMap.get(String(nextField.tableRowGroupId)) || createId();
        }
        if (nextField.tableRowBaseId) {
            nextField.tableRowBaseId = fieldIdMap.get(String(nextField.tableRowBaseId)) || nextId;
        }
        return nextField;
    });
}

function resolveFieldRectPx(field, width, height, preferStoredPx = false) {
    const widthPx = Number(field.widthPx) || (Number(field.wNorm) || 0) * width;
    const heightPx = Number(field.heightPx) || (Number(field.hNorm) || 0) * height;
    const fallbackX = (Number(field.xNorm) || 0) * width;
    const fallbackY = (Number(field.yNorm) || 0) * height;
    return {
        xPx: preferStoredPx ? Number(field.xPx) || fallbackX : fallbackX,
        yPx: preferStoredPx ? Number(field.yPx) || fallbackY : fallbackY,
        widthPx,
        heightPx,
    };
}

function resolveInsertDelta({
    fields,
    width,
    height,
    insertOriginPx,
    defaultOffsetPx,
    preferStoredPx = false,
}) {
    if (!Array.isArray(fields) || fields.length === 0) {
        return { dx: 0, dy: 0 };
    }
    const rects = fields.map((field) => resolveFieldRectPx(field, width, height, preferStoredPx));
    const minX = Math.min(...rects.map((rect) => rect.xPx));
    const minY = Math.min(...rects.map((rect) => rect.yPx));
    const maxX = Math.max(...rects.map((rect) => rect.xPx + rect.widthPx));
    const maxY = Math.max(...rects.map((rect) => rect.yPx + rect.heightPx));
    const groupWidth = Math.max(0, maxX - minX);
    const groupHeight = Math.max(0, maxY - minY);
    if (insertOriginPx && Number.isFinite(insertOriginPx.x) && Number.isFinite(insertOriginPx.y)) {
        return {
            dx: insertOriginPx.x - groupWidth / 2 - minX,
            dy: insertOriginPx.y - groupHeight / 2 - minY,
        };
    }
    if (preferStoredPx) {
        return {
            dx: defaultOffsetPx - minX,
            dy: defaultOffsetPx - minY,
        };
    }
    return {
        dx: defaultOffsetPx,
        dy: defaultOffsetPx,
    };
}

function buildPastedFields({
    payload,
    existingFields = [],
    pageSize,
    pageIndex,
    activeSectionId,
    currentTemplateId,
    existingSectionIds = [],
    createId,
    offsetPx = 8,
    insertOriginPx = null,
}) {
    if (!payload || !Array.isArray(payload.fields) || payload.fields.length === 0) {
        return [];
    }
    const width = Number(pageSize?.width) || 0;
    const height = Number(pageSize?.height) || 0;
    if (!width || !height) {
        return [];
    }
    const sameTemplate = payload.templateId && String(payload.templateId) === String(currentTemplateId);
    const sectionIdSet = new Set(existingSectionIds.map((value) => String(value)));
    const maxSequence = existingFields.reduce(
        (maxValue, field) =>
            field.skipSequence ? maxValue : Math.max(maxValue, Number(field.sequenceNumber) || 0),
        0,
    );
    let sequenceCursor = maxSequence;
    const orderStart = existingFields.length;
    const remappedFields = remapTableRowKeys(payload.fields, createId);
    const { dx, dy } = resolveInsertDelta({
        fields: remappedFields,
        width,
        height,
        insertOriginPx,
        defaultOffsetPx: offsetPx,
        preferStoredPx: false,
    });

    return remappedFields.map((field, index) => {
        const cloned = cloneValue(field);
        const targetSectionId =
            sameTemplate && cloned.sectionId && sectionIdSet.has(String(cloned.sectionId))
                ? cloned.sectionId
                : activeSectionId;
        const shouldSkip = Boolean(cloned.skipSequence);
        if (!shouldSkip) {
            sequenceCursor += 1;
        }
        const sourceRect = resolveFieldRectPx(cloned, width, height, false);
        const nextX = clamp(sourceRect.xPx + dx, 0, Math.max(0, width - sourceRect.widthPx));
        const nextY = clamp(sourceRect.yPx + dy, 0, Math.max(0, height - sourceRect.heightPx));
        return {
            ...cloned,
            pageIndex,
            sectionId: targetSectionId,
            orderIndex: orderStart + index,
            sequenceNumber: shouldSkip
                ? Number.isFinite(Number(cloned.sequenceNumber))
                    ? Number(cloned.sequenceNumber)
                    : 0
                : sequenceCursor,
            xNorm: clamp(nextX / width, 0, 1),
            yNorm: clamp(nextY / height, 0, 1),
        };
    });
}

function buildPresetPayload({ fields = [], pageSize }) {
    const width = Number(pageSize?.width) || 0;
    const height = Number(pageSize?.height) || 0;
    if (!width || !height || fields.length === 0) {
        return { fields: [] };
    }
    const fieldsWithPx = fields.map((field) => ({
        ...cloneValue(field),
        xPx: (Number(field.xNorm) || 0) * width,
        yPx: (Number(field.yNorm) || 0) * height,
        widthPx: (Number(field.wNorm) || 0) * width,
        heightPx: (Number(field.hNorm) || 0) * height,
    }));
    const originX = Math.min(...fieldsWithPx.map((field) => field.xPx));
    const originY = Math.min(...fieldsWithPx.map((field) => field.yPx));

    return {
        fields: fieldsWithPx.map((field) => ({
            ...field,
            pageIndex: 0,
            sectionId: null,
            xPx: field.xPx - originX,
            yPx: field.yPx - originY,
        })),
    };
}

function buildFieldsFromPresetPayload({
    payload,
    existingFields = [],
    pageSize,
    pageIndex,
    activeSectionId,
    createId,
    insertOffsetPx = 16,
    insertOriginPx = null,
}) {
    if (!payload || !Array.isArray(payload.fields) || payload.fields.length === 0) {
        return [];
    }
    const width = Number(pageSize?.width) || 0;
    const height = Number(pageSize?.height) || 0;
    if (!width || !height) {
        return [];
    }
    const maxSequence = existingFields.reduce(
        (maxValue, field) =>
            field.skipSequence ? maxValue : Math.max(maxValue, Number(field.sequenceNumber) || 0),
        0,
    );
    const usedFieldKeys = new Set(
        existingFields
            .map((field) => normalizeFieldKeyBase(field?.key))
            .filter(Boolean),
    );
    let sequenceCursor = maxSequence;
    const orderStart = existingFields.length;
    const remappedFields = remapTableRowKeys(payload.fields, createId);
    const { dx, dy } = resolveInsertDelta({
        fields: remappedFields,
        width,
        height,
        insertOriginPx,
        defaultOffsetPx: insertOffsetPx,
        preferStoredPx: true,
    });

    return remappedFields.map((field, index) => {
        const shouldSkip = Boolean(field.skipSequence);
        if (!shouldSkip) {
            sequenceCursor += 1;
        }
        const sourceRect = resolveFieldRectPx(field, width, height, true);
        const xPx = clamp(sourceRect.xPx + dx, 0, Math.max(0, width - sourceRect.widthPx));
        const yPx = clamp(sourceRect.yPx + dy, 0, Math.max(0, height - sourceRect.heightPx));

        return {
            ...cloneValue(field),
            id: field.id || createId(),
            key: buildUniquePresetFieldKey(field, usedFieldKeys, createId),
            pageIndex,
            sectionId: activeSectionId,
            orderIndex: orderStart + index,
            sequenceNumber: shouldSkip
                ? Number.isFinite(Number(field.sequenceNumber))
                    ? Number(field.sequenceNumber)
                    : 0
                : sequenceCursor,
            xNorm: clamp(xPx / width, 0, 1),
            yNorm: clamp(yPx / height, 0, 1),
            wNorm: clamp(sourceRect.widthPx / width, 0, 1),
            hNorm: clamp(sourceRect.heightPx / height, 0, 1),
        };
    });
}

export {
    buildSelectionClipboardPayload,
    serializeSelectionClipboardPayload,
    parseSelectionClipboardPayload,
    writeSelectionClipboardPayload,
    readSelectionClipboardPayload,
    buildPastedFields,
    buildPresetPayload,
    buildFieldsFromPresetPayload,
};
